import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { StatusPill, formatProgressTone, useFormPalette } from './DynamicFormPrimitives';
import PublicFormShell from './PublicFormShell';
import { StyledSubmitButton } from './PublicFormStyles';
import FormStatusBanner, { resolveFormStatusBannerKey } from './FormStatusBanner';
import { EmailMagicLinkGateScreen, AuthGateScreen, DisabledGateScreen, NotPublishedScreen } from '../../access/PublicAccessGateScreens';
import { createAgentAdapter } from './adapters/agentFormAdapter';
import { createTriggerAdapter } from './adapters/triggerFormAdapter';
import { createTemplateAdapter } from './adapters/templateFormAdapter';
import usePublicForm from './usePublicForm';
import FormFlowStepper from './FormFlow/FormFlowStepper';
import FormAuthenticatedUserChip from './AuthenticatedUserChip';
import FormDraftStatusBar from './FormDraftStatusBar';

export default function PublicFormPage({
  componentId: componentIdProp,
  instanceId: instanceIdProp,
  token: tokenProp,
  preview: previewProp,
  embedded = false,
  adapterKind,
} = {}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const token = tokenProp ?? params.token;
  const isTemplate = adapterKind === 'template';
  // For template routes the stable id lives in params.templateId; reuse the
  // componentId slot so the rest of the page logic stays unchanged.
  const componentId = componentIdProp ?? params.componentId ?? params.templateId;
  const routeInstanceId = instanceIdProp ?? params.instanceId;
  const preview =
    previewProp !== undefined ? previewProp : searchParams.get('preview') === 'true';
  const palette = useFormPalette();
  const theme = useTheme();
  const navigate = useNavigate();

  const [embeddedInstanceId, setEmbeddedInstanceId] = useState(null);
  const instanceId = embedded ? routeInstanceId ?? embeddedInstanceId : routeInstanceId;

  // Capture the instanceId *at mount* for the adapter / resourceId. Once the adapter is
  // created, the onInstanceCreated → navigate flow mutates the URL's instanceId; if we let
  // that flow back into the adapter memo it would re-create the adapter, re-run bootstrap,
  // and the user would see their form flash and re-fetch from the server — feels like a
  // hard refresh right after clicking Save draft. The adapter tracks its own _instanceId
  // internally (set from saveDraft/submit responses), so we don't need to recreate it.
  const initialInstanceIdRef = useRef(instanceId);
  const resourceId = token
    || (initialInstanceIdRef.current ? `${componentId}/${initialInstanceIdRef.current}` : componentId);
  const adapter = useMemo(() => {
    if (token) return createAgentAdapter(token);
    if (isTemplate) {
      return createTemplateAdapter(componentId, initialInstanceIdRef.current, { preview });
    }
    return createTriggerAdapter(componentId, initialInstanceIdRef.current, { preview });
  }, [token, isTemplate, componentId, preview]);

  const onInstanceCreated = useCallback((newInstanceId) => {
    if (!componentId || !newInstanceId) return;
    if (embedded) {
      setEmbeddedInstanceId(newInstanceId);
      return;
    }
    const basePath = isTemplate ? '/form' : '/dynamic-form';
    navigate(`${basePath}/${componentId}/${newInstanceId}`, { replace: true });
  }, [componentId, navigate, embedded, isTemplate]);

  const {
    loading,
    submitting,
    savingDraft,
    form,
    answers,
    validation,
    error,
    gateState,
    submitted,
    readOnly,
    canSubmit,
    canEdit,
    coverImageUrl,
    capabilities,
    onChangeAnswer,
    onFieldBlur,
    onUpload,
    onRemoveFile,
    retryUpload,
    saveDraft,
    submit,
    refresh,
    requestMagicLink,
    onSignedIn,
    autoSaveStatus,
    lastSavedAt,
  } = usePublicForm({ adapter, resourceId, onInstanceCreated });

  // Flow-member routing (Option A).
  //
  // Cold-trigger flow ENTRY (authored flowEntryId == componentId, no FlowSession yet):
  // stay on /dynamic-form/{componentId}. Render the form-flow chrome (stepper) inline
  // via {@code topBar} below. Defer FlowSession creation to submit time —
  // DynamicFormController.submit mints one via HandoffResolver.resolveFromStandalone
  // and handleHandoffEnvelope routes the respondent to /form-flow/{fsid} for step 2+.
  // Keeps the share URL stable across every first-step visit instead of producing a
  // fresh /form-flow/{uuid} each time and requiring /form-flow/start-session to be
  // publicly reachable.
  //
  // In-flight FlowSession (backend /meta emits formSessionId on the response): a
  // FlowSession already exists for this component — either because the respondent is
  // resuming the entry after submit, or because they're landing on a middle/terminal
  // member's URL mid-flow. Either way, route them into the /form-flow/{fsid} shell so
  // history-aware chrome (COMPLETED vs CURRENT, back-nav) takes over; the standalone UI
  // can't show any of that.
  //
  // Guards:
  //  - token-addressed forms (agent share-links) are never flow members; skip.
  //  - embedded renders stay inline; the host owns navigation.
  useEffect(() => {
    if (token || embedded || !form || !componentId) return;
    if (!form.formSessionId) return;
    const path = form.testMode
      ? `/form-flow-test/${form.formSessionId}`
      : `/form-flow/${form.formSessionId}`;
    navigate(path, { replace: true });
  }, [token, embedded, form, componentId, navigate]);

  const authGate = useMemo(() => {
    if (gateState === 'not_published') return <NotPublishedScreen />;
    if (gateState === 'disabled') return <DisabledGateScreen />;
    if (gateState === 'email_required') {
      return <EmailMagicLinkGateScreen onRequestMagicLink={requestMagicLink} />;
    }
    if (gateState === 'auth_required') {
      return <AuthGateScreen onSignedIn={onSignedIn} />;
    }
    return null;
  }, [gateState, requestMagicLink, onSignedIn]);

  const statusBannerKey = resolveFormStatusBannerKey(form);

  // Option A flow-entry chrome. When /meta declared this dynamic_form as the flow entry
  // AND shipped an authored stepper, render the same top-bar surface the FormFlowShell
  // uses for mid-flow members (FormFlowStepper + user chip) so respondents/authors see
  // "Step 1 of N" on the stable /dynamic-form/{componentId} URL. Keyed off flowStepper
  // being present so we degrade gracefully to standalone UI if the stepper lookup
  // failed server-side. Explicit allowBack=false because step 1 has nothing to go back
  // to. Authors can customise the accent colour via the flow's branding on the entry
  // componentData in the future; until then we take the theme default.
  const flowStepperTopBar =
    form?.isFlowEntry
    && Array.isArray(form?.flowStepper)
    && form.flowStepper.length > 1 ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: { xs: 2, sm: 4 },
          py: 1.25,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FormFlowStepper
            steps={form.flowStepper}
            accent={theme.palette.primary.main}
            allowBack={false}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <FormAuthenticatedUserChip inline />
        </Box>
      </Box>
    ) : null;

  return (
    <PublicFormShell
      loading={loading}
      error={error}
      form={form}
      answers={answers}
      validation={validation}
      submitted={submitted}
      submitting={submitting}
      readOnly={readOnly}
      coverImageUrl={coverImageUrl}
      formId={componentId}
      isPreview={!!form?.isPreview}
      onChange={onChangeAnswer}
      onFieldBlur={onFieldBlur}
      onUpload={capabilities.canUpload ? onUpload : undefined}
      onRemoveFile={capabilities.canUpload ? onRemoveFile : undefined}
      retryUpload={capabilities.canUpload ? retryUpload : undefined}
      authGate={authGate}
      topBar={flowStepperTopBar}
      statusBanner={statusBannerKey ? <FormStatusBanner status={statusBannerKey} /> : null}
      headerRightSlot={
        capabilities.hasProgressStatus && form ? (
          <StatusPill
            label={form.progressStatus || 'ACTIVE'}
            tone={formatProgressTone(form.progressStatus || 'ACTIVE')}
          />
        ) : null
      }
      stickyHeader={
        form && (capabilities.canRefresh || capabilities.canSaveDraft || autoSaveStatus || lastSavedAt) ? (
          <>
            <FormDraftStatusBar
              palette={palette}
              autoSaveStatus={autoSaveStatus}
              lastSavedAt={lastSavedAt}
              savingDraft={savingDraft}
              disabled={submitting || readOnly || !canEdit}
              onSaveDraft={capabilities.canSaveDraft ? saveDraft : null}
            />
            {capabilities.canRefresh && (
              <Button
                size="small"
                variant="text"
                onClick={refresh}
                disabled={submitting}
                sx={{
                  textTransform: 'none',
                  color: palette.textSecondary,
                  fontSize: '13px',
                  minWidth: 'auto',
                  '&:hover': { color: palette.textPrimary, bgcolor: 'transparent' },
                }}
              >
                Refresh
              </Button>
            )}
          </>
        ) : null
      }
      actions={
        statusBannerKey ? null : (
          <StyledSubmitButton
            palette={palette}
            isLoading={submitting}
            disabled={submitting || readOnly || !canSubmit}
            onClick={submit}
          >
            {submitting
              ? (form?.submitted ? 'Resubmitting...' : 'Submitting...')
              : (form?.submitted ? 'Resubmit' : 'Submit')}
          </StyledSubmitButton>
        )
      }
    />
  );
}
